import React from 'react';
import { Mail, Phone } from 'lucide-react';

export const PrivacyPolicyContent: React.FC = () => {
  return (
    <div className="space-y-6 text-gray-100">
      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">1. Introduction</h2>
        <p className="leading-relaxed">
          Lead Wealth A.I. ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services and visit our website. Please read this policy carefully to understand our practices regarding your personal data.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">2. Information We Collect</h2>
        <p className="leading-relaxed mb-3">
          We may collect several types of information from and about users of our services:
        </p>
        <h3 className="text-xl font-semibold mb-2 text-blue-200">Personal Information</h3>
        <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
          <li>Name, email address, phone number, and company name</li>
          <li>Business information and project requirements</li>
          <li>Payment and billing information</li>
          <li>Communication preferences and correspondence</li>
        </ul>
        <h3 className="text-xl font-semibold mb-2 text-blue-200">Technical Information</h3>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>IP address, browser type, and operating system</li>
          <li>Pages visited, time spent on pages, and navigation patterns</li>
          <li>Device information and unique identifiers</li>
          <li>Cookies and similar tracking technologies</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">3. How We Use Your Information</h2>
        <p className="leading-relaxed mb-3">
          We use the information we collect for the following purposes:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>To provide, maintain, and improve our services</li>
          <li>To process transactions and send related information</li>
          <li>To communicate with you about services, updates, and promotional offers</li>
          <li>To respond to your inquiries and provide customer support</li>
          <li>To personalize your experience and deliver relevant content</li>
          <li>To analyze usage patterns and optimize our website and services</li>
          <li>To detect, prevent, and address technical issues or security threats</li>
          <li>To comply with legal obligations and protect our rights</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">4. Information Sharing and Disclosure</h2>
        <p className="leading-relaxed mb-3">
          We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li><strong>Service Providers:</strong> With trusted third-party service providers who assist in operating our business (e.g., payment processors, hosting services)</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights, property, or safety</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">5. Data Security</h2>
        <p className="leading-relaxed">
          We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, access controls, and regular security assessments. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">6. Data Retention</h2>
        <p className="leading-relaxed">
          We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">7. Cookies and Tracking Technologies</h2>
        <p className="leading-relaxed">
          We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small files stored on your device that help us analyze web traffic, remember your preferences, and improve functionality. You can control cookie settings through your browser, but disabling cookies may limit certain features of our website.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">8. Your Privacy Rights</h2>
        <p className="leading-relaxed mb-3">
          Depending on your location, you may have certain rights regarding your personal information:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information</li>
          <li><strong>Objection:</strong> Object to processing of your personal information</li>
          <li><strong>Data Portability:</strong> Request transfer of your data to another service</li>
          <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
        </ul>
        <p className="leading-relaxed mt-3">
          To exercise these rights, please contact us using the information provided below.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">9. Third-Party Links</h2>
        <p className="leading-relaxed">
          Our website may contain links to third-party websites or services. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">10. Children's Privacy</h2>
        <p className="leading-relaxed">
          Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected information from a child, we will take steps to delete it promptly.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">11. International Data Transfers</h2>
        <p className="leading-relaxed">
          Your information may be transferred to and processed in countries other than your country of residence. We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-400">12. Changes to This Privacy Policy</h2>
        <p className="leading-relaxed">
          We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of our services after changes are posted constitutes your acceptance of the updated policy.
        </p>
      </section>

      <p className="text-sm text-gray-300 text-center mt-8 pt-6 border-t border-white/20">
        Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      <div className="mt-8 text-center pt-6 border-t border-white/20">
        <h3 className="text-xl font-semibold mb-4 text-yellow-400">Contact Us</h3>
        <p className="text-gray-300 mb-4 text-sm">
          If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="mailto:admin@leadwealth.ai"
            className="flex items-center text-gray-200 hover:text-yellow-400 transition-colors group text-sm"
          >
            <Mail className="w-4 h-4 mr-2 text-yellow-400 group-hover:scale-110 transition-transform" />
            admin@leadwealth.ai
          </a>
          <a
            href="tel:4098935232"
            className="flex items-center text-gray-200 hover:text-yellow-400 transition-colors group text-sm"
          >
            <Phone className="w-4 h-4 mr-2 text-yellow-400 group-hover:scale-110 transition-transform" />
            409-893-5232
          </a>
        </div>
      </div>
    </div>
  );
};
